import { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@twilio-paste/core/button";
import { AttachIcon } from "@twilio-paste/icons/esm/AttachIcon";

import { AppState } from "../store/definitions";
import { hiddenInputStyles } from "./styles/AttachFileButton.styles";
import { validateFiles } from "../utils/validateFiles";
import { addNotification, attachFiles } from "../store/actions/genericActions";

// 文件类型验证函数
const validateFileType = (fileName: string, forbiddenExtensions: string[]): boolean => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (!extension) return true; // 没有扩展名的文件允许上传
    
    // 检查是否在禁止列表中
    return !forbiddenExtensions.includes(extension);
};

// 过滤禁止的文件类型
const filterForbiddenFiles = (files: File[], forbiddenExtensions: string[]): File[] => {
    return files.filter(file => validateFileType(file.name, forbiddenExtensions));
};

export const AttachFileButton = ({ textAreaRef }: { textAreaRef?: React.RefObject<HTMLTextAreaElement> }) => {
    const fileInputRef: React.RefObject<HTMLInputElement> = useRef(null);
    const { attachedFiles, fileAttachmentConfig } = useSelector((state: AppState) => ({
        attachedFiles: state.chat.attachedFiles || [],
        fileAttachmentConfig: state.config.fileAttachment
    }));

    const dispatch = useDispatch();

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files && Array.from(event.target.files);
        if (selectedFiles) {
            // 首先过滤掉禁止的文件类型
            const filteredFiles = fileAttachmentConfig?.forbiddenExtensions 
                ? filterForbiddenFiles(selectedFiles, fileAttachmentConfig.forbiddenExtensions)
                : selectedFiles;

            // 检查是否有文件被过滤掉
            if (filteredFiles.length < selectedFiles.length) {
                const removedCount = selectedFiles.length - filteredFiles.length;
                dispatch(
                    addNotification({
                        id: `FileAttachmentForbiddenTypeNotification_${Math.random()}`,
                        dismissible: true,
                        message: `有 ${removedCount} 个文件因类型限制无法上传。禁止的文件类型包括: ${fileAttachmentConfig?.forbiddenExtensions?.join(
                            ", "
                        )}`,
                        type: "error",
                        timeout: 10000
                    })
                );
            }

            // 如果没有可用的文件，直接返回
            if (filteredFiles.length === 0) {
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                return;
            }

            // 对剩余的文件进行其他验证（大小、数量等）
            const validFiles = validateFiles(filteredFiles, dispatch, attachedFiles, fileAttachmentConfig);
            dispatch(attachFiles(validFiles));
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        textAreaRef?.current?.focus();
    };

    return (
        <Button variant="secondary_icon" size="icon_small" onClick={() => fileInputRef.current?.click()}>
            <input
                style={hiddenInputStyles}
                onChange={onFileChange}
                type="file"
                // 移除 accept 属性限制，允许选择所有文件类型，在后续验证中过滤
                ref={fileInputRef}
                multiple
            />
            <AttachIcon decorative={false} title="Add file attachment" />
        </Button>
    );
};